/**
 * YKD Carousel
 *
 * Progressive enhancement over a native CSS scroll-snap track. The track
 * scrolls and snaps without any of this running — JS only adds the dot
 * indicators, keeps them in sync, and wires them up as controls.
 *
 * No dependencies.
 */

class YkdCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-ykd-track]');
    this.dots = Array.from(this.querySelectorAll('[data-ykd-dot]'));
    this.slides = Array.from(this.querySelectorAll('[data-ykd-slide]'));

    if (!this.track || this.slides.length === 0) return;

    // Dots are authored hidden so a no-JS visitor never sees dead controls.
    const nav = this.querySelector('[data-ykd-nav]');
    if (nav) nav.removeAttribute('hidden');

    this.#observe();
    this.#bindDots();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  #observe() {
    // 60% keeps exactly one slide "active" given the peek of the next.
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = this.slides.indexOf(entry.target);
          if (index !== -1) this.#setActive(index);
        });
      },
      { root: this.track, threshold: 0.6 }
    );

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  #bindDots() {
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        const slide = this.slides[index];
        if (!slide) return;

        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollTo({
          left: slide.offsetLeft - this.track.offsetLeft,
          behavior: reduced ? 'auto' : 'smooth',
        });
      });
    });
  }

  #setActive(index) {
    this.dots.forEach((dot, i) => {
      const active = i === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
      dot.tabIndex = active ? 0 : -1;
    });
  }
}

if (!customElements.get('ykd-carousel')) {
  customElements.define('ykd-carousel', YkdCarousel);
}
